import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiConfig } from '@tenant/helpers';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Address } from 'libs/core/src/lib/models/address.model';
import { Profile } from '../../index';
import { ProfileType } from './profile.model';

export interface HeaderTasks {
  id: number;
  address: Address;
  active_tasks_count: number;
}

export interface HeaderTasksRes {
  tasks_by_deals: HeaderTasks[];
  total: number;
}

export interface WorkQueueData {
  my_tasks: number;
  applications: number;
  deal_approvals?: number;
  accounting?: number;
}

/**
 * Role flag -> work-queue endpoint segment. ORDERED: the first matching flag
 * wins (admin over teamlead over office_manager for dual-role users). Adding a
 * role to the work queue = one row here + the matching backend endpoint
 * (mirrors canViewWorkQueueDashboard's urls in capabilities.csv — the agent
 * feed of canViewWorkQueueData is served separately by tasks()).
 */
const WORK_QUEUE_SEGMENTS: ReadonlyArray<readonly [keyof ProfileType, string]> =
  [
    ['is_company_admin', 'admin'],
    ['is_teamlead', 'teamlead'],
    ['is_office_manager', 'office-manager'],
  ];

@Injectable()
export class HeaderDataService {
  public get workQueueData() {
    return this.workQueueState.asObservable();
  }

  private workQueueState = new BehaviorSubject({});

  constructor(private client: HttpClient) {}

  public tasks(): Observable<HeaderTasksRes> {
    return this.client.get<HeaderTasksRes>(`${ApiConfig.deal}work-queue/agent`);
  }

  public workQueue(user: Profile): Observable<WorkQueueData> {
    const match =
      user && WORK_QUEUE_SEGMENTS.find(([flag]) => !!user.roles?.[flag]);
    if (!match) {
      return of(null);
    }
    return this.client.get<WorkQueueData>(`${ApiConfig.workQueue}/${match[1]}`).pipe(
      tap((data) => {
        if (data) {
          this.workQueueState.next(data);
        }
      })
    );
  }
}
